
import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutGrid, Calendar, Database, Settings, Factory, FileStack, Ticket, Home, UserPlus } from 'lucide-react';
import { useAppStore } from '../store';
import { Role } from '../types';
import { cn } from '../lib/utils';
import { Avatar } from './ui/Avatar';
import { ProfileMenu } from './ProfileMenu';

export const Sidebar = () => {
  const location = useLocation();
  const { user, tickets } = useAppStore();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  const isActive = (path: string) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));

  const openTicketsCount = tickets.filter(t => t.status === 'OPEN' && t.assigneeId === user?.id).length;

  // Only Admin can see onboarding
  const showOnboarding = user?.role === Role.ADMIN;

  const navItems = [
    { icon: Home, label: 'Home', path: '/dashboard' },
    { icon: LayoutGrid, label: 'Projects', path: '/projects' },
    { icon: Ticket, label: 'Tickets', path: '/tickets', badge: openTicketsCount },
    ...(showOnboarding ? [{ icon: UserPlus, label: 'Onboarding', path: '/onboarding' }] : []),
    { icon: Calendar, label: 'Calendar', path: '/calendar' },
    ...(user?.role === Role.FACTORY ? [{ icon: Factory, label: 'Factory', path: '/factory' }] : []),
    { icon: Database, label: 'Database', path: '/database' },
    { icon: FileStack, label: 'Templates', path: '/templates' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  return (
    <aside className="w-16 bg-[var(--sidebar)] dark:bg-slate-900 border-r border-[var(--sidebar-border)] dark:border-slate-800 flex flex-col items-center py-4 flex-shrink-0 z-50">
      <div className="mb-6">
        <Link to="/dashboard" className="h-10 w-10 bg-[var(--sidebar-primary)] dark:bg-blue-600 rounded-xl flex items-center justify-center text-[var(--sidebar-primary-foreground)] shadow-sm">
          <span className="font-bold text-xl">C</span>
        </Link>
      </div>

      <nav className="flex-1 w-full space-y-1.5 px-2">
        {navItems.map(item => {
          const active = isActive(item.path);
          return (
            <Link 
              key={item.path} 
              to={item.path}
              title={item.label}
              className={cn(
                "relative flex items-center justify-center w-full aspect-square rounded-xl transition-all duration-150 group",
                active 
                  ? "bg-[var(--sidebar-primary)] dark:bg-blue-600 text-[var(--sidebar-primary-foreground)] shadow-sm" 
                  : "text-[var(--muted-foreground)] hover:bg-[var(--sidebar-accent)] dark:hover:bg-slate-800 hover:text-[var(--sidebar-accent-foreground)]"
              )}
            >
              <item.icon size={20} strokeWidth={active ? 2.5 : 2} />
              {item.badge ? (
                <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--destructive)] text-[9px] font-bold text-white ring-2 ring-[var(--sidebar)] dark:ring-slate-900">
                  {item.badge}
                </span>
              ) : null}
              {/* Tooltip-like label for small sidebar */}
              <div className="absolute left-14 bg-[var(--popover)] dark:bg-slate-800 text-[var(--popover-foreground)] dark:text-white text-[10px] font-bold px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity border border-[var(--border)] dark:border-slate-700 shadow-xl z-[60]">
                {item.label}
              </div>
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto pt-4 border-t border-[var(--sidebar-border)] dark:border-slate-800 w-full flex justify-center">
        <button
          ref={profileButtonRef}
          onClick={() => {
            console.log('[Sidebar] Profile clicked, current state:', showProfileMenu);
            setShowProfileMenu(!showProfileMenu);
          }}
          className="relative group p-0.5 rounded-full hover:brightness-105 transition-all cursor-pointer"
        >
          <Avatar 
            src={user?.avatar} 
            alt="Profile" 
            name={user?.name}
            size="md" 
            colorScheme={user?.avatarColorScheme || 'blue'}
            className="border border-[var(--sidebar-border)] dark:border-slate-700 shadow-sm" 
          />
          <div className="absolute left-14 bottom-0 bg-[var(--popover)] dark:bg-slate-800 text-[var(--popover-foreground)] dark:text-white text-[10px] font-bold px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity border border-[var(--border)] dark:border-slate-700 shadow-xl z-[60]">
            <div className="font-bold">{user?.name}</div>
            <div className="text-[9px] text-[var(--muted-foreground)] font-medium mt-0.5">{user?.role}</div>
          </div>
        </button>
      </div>

      {/* Profile Menu */}
      <ProfileMenu
        isOpen={showProfileMenu}
        onClose={() => setShowProfileMenu(false)}
        anchorRef={profileButtonRef}
      />
    </aside>
  );
};
